/**
 * Generic concurrency limiter (semaphore).
 *
 * Limits the number of tasks running simultaneously. Tasks that arrive when
 * the pool is full are queued and execute as slots become available. A
 * rejected task releases its slot so the queue advances.
 *
 * Usage:
 *   const limiter = createConcurrencyLimiter(5);
 *   const result = await limiter.run(() => someAsyncWork());
 */

/**
 * Create a concurrency limiter that allows at most `maxConcurrent` tasks to
 * run at once. Tasks beyond the limit are queued and resolved in FIFO order.
 * @param {number} maxConcurrent - Maximum number of simultaneously in-flight
 *   tasks. Must be a positive integer; values ≤ 0 are treated as 1.
 * @returns {{ run: (taskFunc: () => Promise<any>) => Promise<any> }} Limiter with a `run` method.
 */
const createConcurrencyLimiter = (maxConcurrent) => {
  const limit = Math.max(1, Math.floor(maxConcurrent));
  let inFlight = 0;
  const queue = [];

  /**
   * Drain one waiter from the queue (if any) by resolving its held promise.
   * Called after a slot is released so the queue advances.
   */
  const drainQueue = () => {
    if (queue.length > 0) {
      const resolve = queue.shift();
      resolve();
    }
  };

  /**
   * Acquire a slot. Resolves immediately when a slot is free; otherwise
   * enqueues a resolve callback and waits.
   * @returns {Promise<void>}
   */
  const acquireSlot = () => {
    if (inFlight < limit) {
      inFlight += 1;
      return Promise.resolve();
    }
    return new Promise((resolve) => {
      queue.push(() => {
        inFlight += 1;
        resolve();
      });
    });
  };

  /**
   * Release a slot and drain one queued waiter.
   */
  const releaseSlot = () => {
    inFlight -= 1;
    drainQueue();
  };

  /**
   * Run `taskFunc` when a concurrency slot is available. The slot is held for
   * the duration of `taskFunc`'s returned Promise and released in `finally`
   * whether the task resolves or rejects.
   * @param {() => Promise<any>} taskFunc - Zero-argument function returning a Promise.
   * @returns {Promise<any>} Resolves/rejects with the task result.
   */
  const run = async (taskFunc) => {
    await acquireSlot();
    try {
      return await taskFunc();
    } finally {
      releaseSlot();
    }
  };

  return { run };
};

export default createConcurrencyLimiter;
